import { Loading } from "../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import {
  HOLD_PRESETS,
  SUPPORT_ROLE_LABELS,
  SUPPORT_ROLES,
  SUPPORT_ROUTING_LABELS,
  SUPPORT_ROUTINGS,
  type SupportRole,
  type SupportRouting,
  type SupportSettingsDto,
  type SupportTeamRow,
} from "@klantpunt/shared";
import { IconTrash } from "../app/icons";
import { api } from "../lib/api";
import { useMe } from "../lib/auth";
import { useToast } from "../lib/toast";
import { agentsKey, errMsg } from "../features/support/api";
import "../features/support/support.css";
import { SupportGuard } from "./SupportInbox";

const teamKey = ["support", "team"] as const;
const settingsKey = ["support", "settings"] as const;

/** Supportteam en werkafspraken (docs/HELPCENTER.md §9), alleen voor de supportbeheerder. */
export function SupportTeamPage() {
  return (
    <SupportGuard>
      <TeamAdminOnly />
    </SupportGuard>
  );
}

function TeamAdminOnly() {
  const { data: me } = useMe();
  return (
    <div className="page sp-team">
      <p className="label">
        <Link to="/platform">Platformbeheer</Link> · <Link to="/platform/support">Support</Link>
      </p>
      <h1 className="dot">Team en werkafspraken</h1>
      {me?.support === "support_admin" ? (
        <>
          <Team meId={me.user.id} />
          <Settings />
        </>
      ) : (
        <p className="muted">Alleen een supportbeheerder kan het team en de werkafspraken beheren.</p>
      )}
    </div>
  );
}

function Team({ meId }: { meId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: teamKey, queryFn: () => api<SupportTeamRow[]>("/support/team") });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<SupportRole>("support");
  const [error, setError] = useState<string | null>(null);

  async function run(fn: () => Promise<SupportTeamRow[]>, done: string) {
    setError(null);
    try {
      qc.setQueryData(teamKey, await fn());
      void qc.invalidateQueries({ queryKey: agentsKey });
      toast(done);
      return true;
    } catch (e) {
      setError(errMsg(e));
      return false;
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    if (
      await run(
        () => api<SupportTeamRow[]>("/support/team", { method: "POST", body: { email, role } }),
        "Toegevoegd aan het team",
      )
    )
      setEmail("");
  }

  return (
    <section className="panel" aria-labelledby="team-h">
      <h2 id="team-h">Team</h2>
      {q.isPending && <Loading />}
      <ul className="sp-teamlist">
        {(q.data ?? []).map((r) => (
          <li key={r.id}>
            <span className="grow">
              <strong>{r.name ?? r.email}</strong>
              <br />
              <span className="muted small">
                {r.email} · {r.available ? "Beschikbaar" : "Afwezig"}
              </span>
            </span>
            {r.platformAdmin ? (
              <span className="chip">Platformbeheerder</span>
            ) : (
              <select
                aria-label={`Rol van ${r.name ?? r.email}`}
                value={r.role}
                disabled={r.id === meId}
                onChange={(e) =>
                  void run(
                    () =>
                      api<SupportTeamRow[]>(`/support/team/${r.id}`, {
                        method: "PUT",
                        body: { role: e.target.value },
                      }),
                    "Rol gewijzigd",
                  )
                }
              >
                {SUPPORT_ROLES.map((x) => (
                  <option key={x} value={x}>
                    {SUPPORT_ROLE_LABELS[x]}
                  </option>
                ))}
              </select>
            )}
            {r.id !== meId && (
              <button
                type="button"
                className="btn ghost ico"
                aria-label={`${r.name ?? r.email} uit het team halen`}
                onClick={() =>
                  void run(
                    () => api<SupportTeamRow[]>(`/support/team/${r.id}`, { method: "DELETE" }),
                    "Uit het team gehaald",
                  )
                }
              >
                <IconTrash size={18} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>
      <form className="sp-teamadd" onSubmit={(e) => void add(e)}>
        <label className="field">
          <span>E-mailadres</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field">
          <span>Rol</span>
          <select value={role} onChange={(e) => setRole(e.target.value as SupportRole)}>
            {SUPPORT_ROLES.map((x) => (
              <option key={x} value={x}>
                {SUPPORT_ROLE_LABELS[x]}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn primary">
          Toevoegen
        </button>
      </form>
      <p className="muted small">
        Alleen bestaande gebruikers. Een supportbeheerder kan ook beheeracties doen en het team beheren.
      </p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

function Settings() {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: settingsKey, queryFn: () => api<SupportSettingsDto>("/support/settings") });
  const [form, setForm] = useState<SupportSettingsDto | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.data && !form) setForm(q.data);
  }, [q.data, form]);
  if (!form) return null;
  const preset = HOLD_PRESETS.findIndex(
    (p) => p.remind === form.holdRemindDays && p.autoclose === form.holdAutocloseDays,
  );

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await api<SupportSettingsDto>("/support/settings", { method: "PUT", body: form });
      qc.setQueryData(settingsKey, d);
      setForm(d);
      toast("Werkafspraken opgeslagen");
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" aria-labelledby="set-h" onSubmit={(e) => void save(e)}>
      <h2 id="set-h">Werkafspraken</h2>
      <label className="field">
        <span>Nieuwe berichten</span>
        <select value={form.routing} onChange={(e) => setForm({ ...form, routing: e.target.value as SupportRouting })}>
          {SUPPORT_ROUTINGS.map((r) => (
            <option key={r} value={r}>
              {SUPPORT_ROUTING_LABELS[r]}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        <span>Reactietijd (werkuren)</span>
        <input
          type="number"
          min={1}
          max={240}
          value={form.responseTimeHours}
          onChange={(e) => setForm({ ...form, responseTimeHours: Number(e.target.value) })}
          required
        />
      </label>
      <label className="field">
        <span>In de wacht</span>
        <select
          value={preset}
          onChange={(e) => {
            const p = HOLD_PRESETS[Number(e.target.value)]!;
            setForm({ ...form, holdRemindDays: p.remind, holdAutocloseDays: p.autoclose });
          }}
        >
          {preset === -1 && (
            <option value={-1}>
              Eigen instelling ({form.holdRemindDays ?? "–"}/{form.holdAutocloseDays ?? "–"} dagen)
            </option>
          )}
          {HOLD_PRESETS.map((p, i) => (
            <option key={p.label} value={i}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <div className="btnrow">
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? "Opslaan…" : "Opslaan"}
        </button>
      </div>
    </form>
  );
}
